import { render } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

function Mark() {
  return <svg aria-hidden="true" viewBox="0 0 73 95"><use href="#pagecraft-mark" /></svg>;
}

function ViewportSwitch() {
  const [viewport, setViewport] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  useEffect(() => {
    const editor = document.querySelector<HTMLElement>('.hero .editor');
    if (editor) editor.dataset.preview = viewport;
  }, [viewport]);

  return (
    <div class="viewport-switch review-devices" role="group" aria-label="Preview width">
      {(['desktop', 'tablet', 'mobile'] as const).map(value => (
        <button
          type="button"
          class={viewport === value ? 'active' : ''}
          aria-label={`${value} preview`}
          aria-pressed={viewport === value}
          onClick={() => setViewport(value)}
          key={value}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><use href={`#review-icon-${value === 'desktop' ? 'monitor' : value === 'mobile' ? 'smartphone' : 'tablet'}`} /></svg>
          <span>{value[0].toUpperCase() + value.slice(1)}</span>
        </button>
      ))}
    </div>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('');
  const shell = useRef<HTMLDivElement>(null);
  const navigation = useRef<HTMLElement>(null);
  const focusMenu = useRef(false);
  const menuButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        setOpen(false);
        menuButton.current?.focus();
      }
    };
    const closeOutside = (event: PointerEvent) => {
      if (open && shell.current && !shell.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    document.addEventListener('pointerdown', closeOutside);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('pointerdown', closeOutside);
    };
  }, [open]);

  useEffect(() => {
    const mobileMenu = window.matchMedia('(max-width: 56.24rem)');
    const closeAtDesktop = (event: MediaQueryListEvent) => {
      if (!event.matches) setOpen(false);
    };

    document.documentElement.classList.toggle('menu-open', open);
    if (open && focusMenu.current) {
      window.requestAnimationFrame(() => navigation.current?.querySelector<HTMLAnchorElement>('a')?.focus());
    }
    focusMenu.current = false;
    mobileMenu.addEventListener('change', closeAtDesktop);

    return () => {
      document.documentElement.classList.remove('menu-open');
      mobileMenu.removeEventListener('change', closeAtDesktop);
    };
  }, [open]);

  useEffect(() => {
    const header = document.querySelector<HTMLElement>('.site-header');
    const sections = ['workflow', 'capabilities', 'ownership']
      .map(id => document.getElementById(id))
      .filter((section): section is HTMLElement => Boolean(section));

    const updateProgress = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      header?.style.setProperty('--page-progress', String(progress));
      header?.toggleAttribute('data-scrolled', window.scrollY > 18);
    };
    const observer = new IntersectionObserver(entries => {
      const visible = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setActive((visible.target as HTMLElement).id);
    }, { rootMargin: '-22% 0px -62% 0px', threshold: [0, .2, .5] });

    sections.forEach(section => observer.observe(section));
    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateProgress);
      window.removeEventListener('resize', updateProgress);
    };
  }, []);

  return (
    <div class="header-shell" ref={shell}>
      <a class="brand" href="#main" aria-label="Pagecraft home"><Mark /><span>Pagecraft</span></a>
      <nav class="nav" id="site-nav" aria-label="Primary navigation" data-open={open || undefined} ref={navigation}>
        <a href="#workflow" aria-current={active === 'workflow' ? 'location' : undefined} onClick={() => setOpen(false)}>How it works</a>
        <a href="#capabilities" aria-current={active === 'capabilities' ? 'location' : undefined} onClick={() => setOpen(false)}>What it handles</a>
        <a href="#ownership" aria-current={active === 'ownership' ? 'location' : undefined} onClick={() => setOpen(false)}>Why Pagecraft</a>
        <a href="https://mockup.itspagecraft.com/" onClick={() => setOpen(false)}>Mockup Builder</a>
        <button class="nav-cta" type="button" disabled>Coming soon</button>
      </nav>
      <button class="button button--green header-cta" type="button" disabled>Coming soon</button>
      <button
        ref={menuButton}
        class="menu-button"
        type="button"
        aria-expanded={open}
        aria-controls="site-nav"
        aria-label={open ? 'Close menu' : 'Open menu'}
        onClick={event => {
          focusMenu.current = event.detail === 0;
          setOpen(value => !value);
        }}
      >
        <svg class="menu-icon" aria-hidden="true"><use href="#menu" /></svg>
        <svg class="close-icon" aria-hidden="true"><use href="#close" /></svg>
      </button>
    </div>
  );
}

const apiSections = [
  { id: 'overview', label: 'Overview' },
  { id: 'discovery', label: 'Discovery' },
  { id: 'wordpress', label: 'WordPress' },
  { id: 'mcp', label: 'MCP' },
  { id: 'ownership', label: 'Access' },
  { id: 'errors', label: 'Errors' }
];

function ApiHeader() {
  return (
    <>
      <a class="brand" href="../" aria-label="Pagecraft home">
        <Mark />
        <span>Pagecraft</span>
      </a>
      <span class="api-header__label">API documentation</span>
      <button class="button button--green api-header__cta" type="button" disabled>Coming soon</button>
    </>
  );
}

function ApiIndex() {
  const [active, setActive] = useState('overview');

  useEffect(() => {
    const sections = apiSections
      .map(({ id }) => document.getElementById(id))
      .filter((section): section is HTMLElement => Boolean(section));
    let frame = 0;
    const updateActive = () => {
      const headerHeight = document.querySelector<HTMLElement>('.api-header')?.offsetHeight ?? 0;
      const readingLine = headerHeight + 24;
      const current = sections.reduce((match, section) => (
        section.getBoundingClientRect().top <= readingLine ? section : match
      ), sections[0]);
      if (current) setActive(current.id);
      frame = 0;
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(updateActive);
    };

    updateActive();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, []);

  return (
    <>
      <p>On this page</p>
      <nav>
        {apiSections.map(section => (
          <a
            href={`#${section.id}`}
            aria-current={active === section.id ? 'location' : undefined}
            onClick={() => setActive(section.id)}
            key={section.id}
          >
            {section.label}
          </a>
        ))}
      </nav>
    </>
  );
}

function ApiFooter() {
  return (
    <>
      <a class="brand footer-brand" href="../" aria-label="Pagecraft home">
        <Mark />
        <span>Pagecraft</span>
      </a>
      <nav aria-label="Footer navigation">
        <a href="../">Home</a>
        <a href="./" aria-current="page">API</a>
      </nav>
      <p>Build visually. Publish deliberately. Keep what you make.</p>
    </>
  );
}

const workflow = [
  {
    title: 'Choose a useful starting point',
    body: 'Begin with a full-page template, a ready-made section, or an empty canvas. Every starting point inherits your project’s own type and colour system.'
  },
  {
    title: 'Shape it where it lives',
    body: 'Edit copy, layout, imagery, responsive styles, forms, and reusable components on the canvas. The structure stays visible while you work.'
  },
  {
    title: 'Review before you release',
    body: 'Pagecraft checks missing destinations, image descriptions, heading structure, contrast, and the other details that make a site dependable.'
  },
  {
    title: 'Publish a version you can trust',
    body: 'A draft stays private until you publish it. The release is deliberate, portable, and separate from whatever you change next.'
  }
];

function Workflow() {
  const list = useRef<HTMLOListElement>(null);
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(() => new Set<number>());

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setVisible(new Set(workflow.map((_, index) => index)));
      return;
    }

    let frame = 0;
    const update = () => {
      const rows = Array.from(list.current?.children ?? []) as HTMLElement[];
      const header = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 68;
      const line = header + window.innerHeight * 0.3;
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;
      const seen = new Set<number>();

      rows.forEach((row, index) => {
        const rect = row.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.92 && rect.bottom > 0) seen.add(index);
        const nextDistance = Math.abs(rect.top - line);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = index;
        }
      });
      setCurrent(nearest);
      setVisible(seen);
      frame = 0;
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, []);

  return (
    <>
      <div class="section-heading">
        <h2>A faster route from idea to live site.</h2>
        <p>Start with more than an empty box, make every detail yours, and publish only when the page is ready.</p>
      </div>
      <ol class="process motion-ready" ref={list}>
        {workflow.map((step, index) => (
          <li class={`${index === current ? 'is-current ' : ''}${visible.has(index) ? 'is-visible' : ''}`} key={step.title}>
            <span class="process-number">{String(index + 1).padStart(2, '0')}</span>
            <div><h3>{step.title}</h3><p>{step.body}</p></div>
          </li>
        ))}
      </ol>
    </>
  );
}

function RevealSystem() {
  useEffect(() => {
    const groups = [
      { section: '.direct-edit', items: ['.direct-edit-heading > *', '.type-canvas'] },
      { section: '.fast-start', items: ['.fast-start-copy > *', '.start-preview'] },
      { section: '.capabilities', items: ['.capability-intro > *', '.capability-card', '.capability-delivery h3', '.capability-delivery p', '.capability-delivery > .button'] },
      { section: '.native-wordpress', items: ['.native-wordpress-heading > *', '.wordpress-admin-stage'] },
      { section: '.ownership', items: ['.release-heading > *', '.working-surface', '.release-switch', '.release-output'] },
      { section: '.try-pagecraft', items: ['.closing-inner > *'] }
    ];
    const nodes = groups.flatMap(({ section, items }) => {
      const root = document.querySelector<HTMLElement>(section);
      if (!root) return [];
      const seen = new Set<HTMLElement>();
      return items.flatMap(selector => Array.from(root.querySelectorAll<HTMLElement>(selector)))
        .filter(node => {
          if (seen.has(node)) return false;
          seen.add(node);
          return true;
        })
        .map((node, index) => {
          node.dataset.motionItem = node.matches('.type-canvas, .start-preview, .capability-card, .wordpress-admin-stage, .working-surface, .release-output') ? 'media' : 'text';
          node.style.setProperty('--motion-delay', `${Math.min(index * 70, 280)}ms`);
          return node;
        });
    });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach(node => node.dataset.motionVisible = 'true');
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          (entry.target as HTMLElement).dataset.motionVisible = 'true';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    nodes.forEach(node => observer.observe(node));
    return () => observer.disconnect();
  }, []);
  return null;
}

const header = document.getElementById('site-header-app');
const workflowRoot = document.getElementById('workflow-app');
const viewportRoot = document.getElementById('viewport-switch-app');
const framework = document.getElementById('framework-root');
const apiHeader = document.getElementById('api-header-app');
const apiIndex = document.getElementById('api-index-app');
const apiFooter = document.getElementById('api-footer-app');

if (header) render(<SiteHeader />, header);
if (workflowRoot) render(<Workflow />, workflowRoot);
if (viewportRoot) render(<ViewportSwitch />, viewportRoot);
if (framework) render(<RevealSystem />, framework);
if (apiHeader) render(<ApiHeader />, apiHeader);
if (apiIndex) render(<ApiIndex />, apiIndex);
if (apiFooter) render(<ApiFooter />, apiFooter);
document.documentElement.classList.add('framework-ready');
